import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
// import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
// import api from '../../services/api';

// Offline Payment Component
const OfflinePaymentModal = ({ planName, planPrice, gstRate, onCancel, offlinePaymentSettings }) => {
  // const [showInstructions, setShowInstructions] = useState(false);
  const { settings } = useSettings();

  // Get theme primary color from settings
  const themePrimaryColor = settings.theme_primary_color || '#0d6efd';
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // Calculate pricing with GST
  const basePrice = parseFloat(planPrice) || 0;
  const gstAmount = (basePrice * (gstRate || 18)) / 100;
  const totalAmount = basePrice + gstAmount;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
            <h5 className="modal-title">
              <i className="fas fa-money-bill-wave me-2"></i>
              Offline Payment - {planName} Plan
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}
              style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
            ></button>
          </div>
          <div className="modal-body">
            {/* Pricing Summary */}
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-receipt me-2"></i>
                  Payment Summary
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Plan:</span>
                      <strong>{planName}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Base Price:</span>
                      <span>₹{basePrice.toFixed(2)}</span>
                    </div>
                    {gstAmount > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span>GST ({gstRate}%):</span>
                        <span>₹{gstAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <hr />
                    <div className="d-flex justify-content-between">
                      <strong>Total Amount:</strong>
                      <strong style={{ color: themePrimaryColor }}>₹{totalAmount.toFixed(2)}</strong>
                    </div>
                  </div>
                  <div className="col-md-6 text-center">
                    {offlinePaymentSettings?.offline_payment_qr_code && (
                      <div>
                        <h6>Scan QR Code to Pay</h6>
                        <img
                          src={offlinePaymentSettings.offline_payment_qr_code.startsWith('http')
                            ? offlinePaymentSettings.offline_payment_qr_code
                            : `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}${offlinePaymentSettings.offline_payment_qr_code}`
                          }
                          alt="Payment QR Code"
                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                          className="border rounded"
                        />
                      </div>
                    )}
                    {!offlinePaymentSettings?.offline_payment_qr_code && (
                      <div className="text-muted">
                        <i className="fas fa-qrcode fa-3x mb-2"></i>
                        <p>QR Code not configured</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            {(offlinePaymentSettings?.offline_payment_bank_name ||
              offlinePaymentSettings?.offline_payment_account_number) && (
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-university me-2"></i>
                      Bank Transfer Details
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {offlinePaymentSettings.offline_payment_bank_name && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-muted">Bank Name</label>
                          <div className="fw-bold">{offlinePaymentSettings.offline_payment_bank_name}</div>
                        </div>
                      )}
                      {offlinePaymentSettings.offline_payment_account_holder && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-muted">Account Holder</label>
                          <div className="fw-bold">{offlinePaymentSettings.offline_payment_account_holder}</div>
                        </div>
                      )}
                      {offlinePaymentSettings.offline_payment_account_number && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-muted">Account Number</label>
                          <div className="fw-bold font-monospace">{offlinePaymentSettings.offline_payment_account_number}</div>
                        </div>
                      )}
                      {offlinePaymentSettings.offline_payment_ifsc_code && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label text-muted">IFSC Code</label>
                          <div className="fw-bold font-monospace">{offlinePaymentSettings.offline_payment_ifsc_code}</div>
                        </div>
                      )}
                      {offlinePaymentSettings.offline_payment_upi_id && (
                        <div className="col-12 mb-3">
                          <label className="form-label text-muted">UPI ID</label>
                          <div className="fw-bold font-monospace">{offlinePaymentSettings.offline_payment_upi_id}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Payment Instructions */}
            <div className="card">
              <div className="card-header bg-warning text-dark">
                <h6 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Payment Instructions
                </h6>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <i className="fas fa-lightbulb me-2"></i>
                  <strong>Important:</strong> After making the payment, please contact our support team with your transaction details for account activation.
                </div>

                {offlinePaymentSettings?.offline_payment_instructions && (
                  <div className="mb-3">
                    <p className="mb-2">{offlinePaymentSettings.offline_payment_instructions}</p>
                  </div>
                )}

                <div className="border-top pt-3">
                  <h6>Steps to complete payment:</h6>
                  <ol className="mb-0">
                    <li>Transfer <strong>₹{totalAmount.toFixed(2)}</strong> to the above bank account or scan the QR code</li>
                    <li>Keep the transaction receipt/screenshot</li>
                    <li>Contact support with your transaction details</li>
                    <li>Your account will be activated within 24 hours of verification</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Close
            </button>
            <button
              type="button"
              className="btn"
              style={{
                backgroundColor: themePrimaryColor,
                borderColor: themePrimaryColor,
                color: 'white'
              }}
              onClick={() => {
                toast.info('Please complete the payment using the details above and contact support for activation.');
                onCancel();
              }}
            >
              <i className="fas fa-check me-2"></i>
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stripe payment form component
const CheckoutForm = ({ planName, planPrice, gstRate, onSuccess, onCancel, setLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const { settings } = useSettings();

  // Get theme primary color from settings
  const themePrimaryColor = settings.theme_primary_color || '#0d6efd';

  // Calculate pricing with GST
  const basePrice = parseFloat(planPrice) || 0;
  const gstAmount = (basePrice * (gstRate || 18)) / 100;
  const totalAmount = basePrice + gstAmount;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe.js has not loaded yet. Please try again.');
      return;
    }

    setProcessing(true);
    setError(null);
    setLoading(true);

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        setProcessing(false);
        setLoading(false);
        return;
      }

      console.log('Payment method created:', paymentMethod.id);

      // Call the upgrade subscription API
      const response = await axios.post('/api/saas/membership/upgrade', {
        payment_method_id: paymentMethod.id,
        plan_name: planName
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('Upgrade response:', response.data);

      if (response.data.success) {
        toast.success('Subscription upgraded successfully!');
        onSuccess();
      } else {
        setError(response.data.message || 'Upgrade failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || err.message || 'Payment failed');
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Pricing Summary */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0">Payment Summary</h6>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-between mb-2">
            <span>Plan:</span>
            <strong>{planName}</strong>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span>Base Price:</span>
            <span>₹{basePrice.toFixed(2)}</span>
          </div>
          {gstAmount > 0 && (
            <div className="d-flex justify-content-between mb-2">
              <span>GST ({gstRate}%):</span>
              <span>₹{gstAmount.toFixed(2)}</span>
            </div>
          )}
          <hr />
          <div className="d-flex justify-content-between">
            <strong>Total Amount:</strong>
            <strong style={{ color: themePrimaryColor }}>₹{totalAmount.toFixed(2)}</strong>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label">Card Details</label>
        <div className="p-3 border rounded">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary me-2"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn"
          style={{
            backgroundColor: themePrimaryColor,
            borderColor: themePrimaryColor,
            color: 'white'
          }}
          disabled={processing || !stripe}
        >
          {processing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Processing...
            </>
          ) : (
            `Pay ₹${totalAmount.toFixed(2)}`
          )}
        </button>
      </div>
    </form>
  );
};

// Get Stripe promise
const getStripePromise = () => {
  const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  if (!stripePublicKey) {
    console.warn('Stripe public key not found in environment variables');
    return null;
  }
  return loadStripe(stripePublicKey);
};

// Initialize Stripe promise
const stripePromise = getStripePromise();

const Membership = () => {
  // const { currentUser } = useContext(AuthContext);
  const { settings } = useSettings();
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [error, setError] = useState(null);
  const [proPlans, setProPlans] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showOfflinePaymentModal, setShowOfflinePaymentModal] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(true);
  const [offlinePaymentSettings, setOfflinePaymentSettings] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!stripePromise) {
      setStripeError('Stripe is not properly configured. Please contact support.');
    }
    fetchSubscriptionDetails();
    fetchProPlans();
    fetchOfflinePaymentSettings();
  }, []);

  const fetchOfflinePaymentSettings = async () => {
    try {
      const response = await axios.get('/api/saas/settings', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data && response.data.success) {
        setOfflinePaymentSettings(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching offline payment settings:', err);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get organization subscription details
      const response = await axios.get('/api/saas/membership/details', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('Membership API response:', response.data);

      if (response.data && response.data.success) {
        setSubscription(response.data.data.subscription);
        setPlan(response.data.data.plan);
        // Set payment gateway enabled flag (default true)
        setPaymentGatewayEnabled(response.data.data.payment_gateway_enabled !== false);

        // Get payment history
        const paymentResponse = await axios.get('/api/saas/membership/payment-history', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        console.log('Payment history API response:', paymentResponse.data);

        if (paymentResponse.data && paymentResponse.data.success) {
          setPaymentHistory(paymentResponse.data.data);
        }
      } else {
        setError(response.data?.message || 'No active subscription found. Please contact your administrator.');
        toast.error(response.data?.message || 'No active subscription found');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      setError('Failed to load subscription details. Please try again later.');
      setLoading(false);
    }
  };

  const fetchProPlans = async () => {
    try {
      const response = await axios.get('/api/saas/subscriptions/plans', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('Plans API response:', response.data);

      if (response.data && Array.isArray(response.data)) {
        // Filter for Pro plans only
        const proPlansOnly = response.data.filter(plan => plan.name === 'Pro');
        setProPlans(proPlansOnly);
      }
    } catch (err) {
      console.error('Error fetching Pro plans:', err);
      toast.error('Failed to load upgrade plans');
    }
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    setUpgradeSuccess(true);
    // Refresh subscription details
    setTimeout(() => {
      fetchSubscriptionDetails();
    }, 2000);
  };

  // Helper function to get subscription status
  const getSubscriptionStatus = () => {
    if (!subscription) return 'Unknown';

    const now = new Date();
    const endDate = new Date(subscription.end_date);

    if (subscription.status === 'cancelled') return 'Cancelled';
    if (subscription.payment_status === 'failed') return 'Payment Failed';
    if (endDate < now) return 'Expired';
    return 'Active';
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'Expired': return 'bg-secondary';
      case 'Cancelled': return 'bg-danger';
      case 'Payment Failed': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  };

  // Calculate pricing with GST
  const calculatePricing = (basePrice, gstRate = 18) => {
    const base = parseFloat(basePrice) || 0;
    const gst = (base * gstRate) / 100;
    const total = base + gst;
    return {
      basePrice: base,
      gstAmount: gst,
      gstRate: gstRate,
      totalPrice: total
    };
  };

  // Render the upgrade modal
  const renderUpgradeModal = () => {
    const proPlan = proPlans[0]; // Take the first Pro plan
    if (!proPlan) return null;

    if (stripeError) {
      return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">Payment Configuration Error</h5>
                <button type="button" className="btn-close"
                  onClick={() => setShowUpgradeModal(false)}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {stripeError}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUpgradeModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const closeIconColor = settings.block_title_color || '#8B4513';
    const closeIconUrl = getCloseIconUrl(closeIconColor);

    return (
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
              <h5 className="modal-title">Upgrade to {proPlan.name} Plan</h5>
              <button type="button" className="btn-close" onClick={() => setShowUpgradeModal(false)}
                style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
              ></button>
            </div>
            <div className="modal-body">
              {stripePromise ? (
                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    planName={proPlan.name}
                    planPrice={proPlan.price}
                    gstRate={proPlan.gst_rate || 18}
                    onSuccess={handleUpgradeSuccess}
                    onCancel={() => setShowUpgradeModal(false)}
                    loading={loading}
                    setLoading={setLoading}
                  />
                </Elements>
              ) : (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Payment system is not properly configured. Please contact support.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the success message if upgrade was successful
  const renderUpgradeSuccess = () => {
    if (!upgradeSuccess) return null;

    return (
      <div className="alert alert-success alert-dismissible fade show" role="alert">
        <strong>Success!</strong> Your subscription has been upgraded to the Pro plan.
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="alert"
          aria-label="Close"
          onClick={() => setUpgradeSuccess(false)}
        ></button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  const pricing = plan ? calculatePricing(plan.price, plan.gst_rate || 18) : null;

  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle
        icon="fa-credit-card"
        title="Membership & Billing"
        className="mb-4"
      />
      <p className="mb-4 text-muted">Manage your subscription and view payment history</p>

      {renderUpgradeSuccess()}

      {subscription && plan && (
        <>
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="card shadow mb-4">
                <div className="card-header py-3 admin-section">
                  <h5 className="m-0 font-weight-bold">Current Subscription</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-4">
                        <h4 className="text-primary">{plan.name} Plan</h4>
                        <p className="text-muted mb-3">{plan.description || 'No description available'}</p>

                        <div className="mb-3">
                          <h5 className="font-weight-bold">Status</h5>
                          <span className={`badge ${getStatusBadgeClass(getSubscriptionStatus())}`}>
                            {getSubscriptionStatus()}
                          </span>
                        </div>

                        <div className="mb-3">
                          <h5 className="font-weight-bold">Subscription Period</h5>
                          <p>{new Date(subscription.start_date).toLocaleDateString()} - {new Date(subscription.end_date).toLocaleDateString()}</p>
                        </div>

                        {plan.features && plan.features.length > 0 && (
                          <div className="mb-4">
                            <h5 className="font-weight-bold">Features</h5>
                            <ul className="list-unstyled">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="mb-1">
                                  <i className="fas fa-check text-success me-2"></i> {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {plan?.name === 'Free' && (
                          paymentGatewayEnabled ? (
                            <button
                              className="btn mt-3"
                              style={{
                                backgroundColor: settings.theme_primary_color || '#0d6efd',
                                borderColor: settings.theme_primary_color || '#0d6efd',
                                color: 'white'
                              }}
                              onClick={() => setShowUpgradeModal(true)}
                            >
                              <i className="fas fa-arrow-up me-2"></i> Upgrade to Pro
                            </button>
                          ) : (
                            <button
                              className="btn btn-warning mt-3"
                              onClick={() => setShowOfflinePaymentModal(true)}
                            >
                              <i className="fas fa-money-bill-wave me-2"></i> Upgrade via Offline Payment
                            </button>
                          )
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      {/* Modern Pricing Card */}
                      <div
                        className="card h-100 shadow-sm border-0"
                        style={{
                          background: `linear-gradient(135deg, ${settings.theme_primary_color || '#0d6efd'}15, ${settings.theme_primary_color || '#0d6efd'}05)`,
                          borderLeft: `4px solid ${settings.theme_primary_color || '#0d6efd'}`
                        }}
                      >
                        <div className="card-body text-center d-flex flex-column justify-content-between">
                          {/* Pricing Section */}
                          <div>
                            <div className="mb-3">
                              <span className="badge rounded-pill px-3 py-2" style={{
                                backgroundColor: settings.theme_primary_color || '#0d6efd',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                CURRENT PLAN
                              </span>
                            </div>

                            {pricing && (
                              <>
                                {/* Base Price */}
                                <div className="mb-2">
                                  <span className="h2 fw-bold" style={{ color: settings.theme_primary_color || '#0d6efd' }}>
                                    ₹{pricing.basePrice.toLocaleString('en-IN')}
                                  </span>
                                  <span className="text-muted ms-1">/{plan?.billing_cycle || 'month'}</span>
                                </div>

                                {/* GST Breakdown */}
                                {pricing.gstAmount > 0 && (
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light rounded">
                                      <small className="text-muted">GST ({plan.gst_rate || 18}%)</small>
                                      <small className="fw-semibold">₹{pricing.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</small>
                                    </div>
                                  </div>
                                )}

                                {/* Total Price */}
                                <div
                                  className="p-3 rounded mb-3"
                                  style={{
                                    backgroundColor: `${settings.theme_primary_color || '#0d6efd'}10`,
                                    border: `1px solid ${settings.theme_primary_color || '#0d6efd'}30`
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <span className="fw-bold">Total Amount</span>
                                    <span className="h5 fw-bold mb-0" style={{ color: settings.theme_primary_color || '#0d6efd' }}>
                                      ₹{pricing.totalPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Support Section */}
                          <div className="mt-auto">
                            <div className="border-top pt-3">
                              <p className="text-muted small mb-2">Need assistance?</p>
                              <button
                                className="btn btn-sm w-100"
                                style={{
                                  borderColor: settings.theme_primary_color || '#0d6efd',
                                  color: settings.theme_primary_color || '#0d6efd',
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = settings.theme_primary_color || '#0d6efd';
                                  e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = settings.theme_primary_color || '#0d6efd';
                                }}
                                onClick={() => navigate('/support')}
                              >
                                <i className="fas fa-headset me-2"></i>
                                Contact Support
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12 col-md-12">
              <div className="card shadow mb-4">
                <div className="card-header py-3 admin-section">
                  <h5 className="m-0 font-weight-bold">Billing Information</h5>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <h5 className="font-weight-bold">Payment Method</h5>
                    <p>{subscription.payment_method === 'stripe' ? 'Credit Card (Stripe)' :
                      subscription.payment_method === 'paypal' ? 'PayPal' :
                        subscription.payment_method === 'cash' ? 'Cash/Bank Transfer' : 'Manual Payment'}</p>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-weight-bold">Payment Status</h5>
                    <span className={`badge bg-${subscription.payment_status === 'paid' ? 'success' :
                      (subscription.payment_status === 'pending' ? 'warning text-dark' : 'danger')}`}>
                      {subscription.payment_status || 'Unknown'}
                    </span>
                  </div>

                  {getSubscriptionStatus() === 'Active' && plan?.billing_cycle === 'monthly' && (
                    <div className="mb-4">
                      <h5 className="font-weight-bold">Next Billing</h5>
                      <p>{new Date(subscription.end_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          <div className="row">
            <div className="col-12">
              <div className="card shadow mb-4">
                <div className="card-header py-3 admin-section">
                  <h5 className="m-0 font-weight-bold">Payment History</h5>
                </div>
                <div className="card-body">
                  {paymentHistory.length === 0 ? (
                    <p className="text-center">No payment history available.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Plan</th>
                            <th>Period</th>
                            <th>Status</th>
                            <th>Payment Method</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentHistory.map(payment => (
                            <tr key={payment.id}>
                              <td>{new Date(payment.date).toLocaleDateString()}</td>
                              <td>₹{parseFloat(payment.amount).toFixed(2)}</td>
                              <td>{payment.plan_name}</td>
                              <td>
                                {new Date(payment.period_start).toLocaleDateString()} - {new Date(payment.period_end).toLocaleDateString()}
                              </td>
                              <td>
                                <span className={`badge bg-${payment.status === 'paid' ? 'success' :
                                  (payment.status === 'pending' ? 'warning text-dark' : 'danger')}`}>
                                  {payment.status}
                                </span>
                              </td>
                              <td>{payment.payment_method}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && renderUpgradeModal()}

      {/* Offline Payment Modal */}
      {showOfflinePaymentModal && proPlans[0] && (
        <OfflinePaymentModal
          planName={proPlans[0].name}
          planPrice={proPlans[0].price}
          gstRate={proPlans[0].gst_rate || 18}
          onCancel={() => setShowOfflinePaymentModal(false)}
          offlinePaymentSettings={offlinePaymentSettings}
        />
      )}
    </div>
  );
};

export default Membership; 